/**
 * Fereastra modala, peste `<dialog>` nativ. Pe telefon e o FOAIE care urca de
 * jos (cu maner si bara de actiuni lipita), pe ecran mare un dialog centrat —
 * aceeasi mecanica pe care o folosesc aplicatiile de livrare pentru fisa
 * produsului, adresa si filtre.
 *
 * Motivul alegerii lui `<dialog>`: `showModal()` aduce din browser exact
 * partile pe care o reimplementare le rateaza aproape intotdeauna — capcana de
 * focus (Tab nu iese din fereastra), Escape, restul paginii marcat `inert`
 * pentru cititoarele de ecran, si stratul superior, care scapa de orice
 * `overflow: hidden` sau `z-index` al parintilor. O varianta pe `div` ar fi
 * cerut toate astea scrise de mana, iar cea care se uita mereu e `inert`:
 * fereastra arata corect, dar un cititor de ecran continua sa citeasca meniul
 * de dedesubt.
 *
 * Ce ramane de facut aici, fiindca nativul NU le da:
 *  - inchiderea la clic pe fundal;
 *  - blocarea derularii paginii din spate cat timp fereastra e deschisa;
 *  - readucerea focusului unde era (`showModal` il da, `close` il returneaza —
 *    dar doar daca elementul care l-a deschis mai exista, deci se retine).
 */

import { useEffect, useRef, type ReactNode } from "react";

import { IconClose } from "./icons";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Titlul ferestrei. Legat prin `aria-labelledby`, deci nu e optional. */
  title: string;
  children: ReactNode;
  /** Bara de actiuni, lipita jos: ramane vizibila cat timp continutul se deruleaza. */
  footer?: ReactNode;
  /** Latimea pe ecran mare. */
  size?: "md" | "lg";
  /**
   * Fara antetul cu titlu; titlul ramane doar pentru cititoarele de ecran.
   * Pentru fisele care isi pun titlul peste poza de deschidere.
   */
  hideHeader?: boolean;
  /** Continut fara margini (poza de deschidere pana la margine). */
  flush?: boolean;
}

export function Modal({ open, onClose, title, children, footer, size = "md", hideHeader = false, flush = false }: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const restoreFocusTo = useRef<Element | null>(null);
  /** Gestul care duce la `click` a INCEPUT pe fundal? Vezi `onPointerDown`. */
  const pressedBackdrop = useRef(false);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;

    if (open) {
      // Elementul care a deschis fereastra se retine SEPARAT de deschiderea ei.
      // Legate (`if (!dialog.open) { retine; showModal() }`), a doua invocare a
      // efectului din `<StrictMode>` gasea dialogul deja deschis, sarea peste
      // ramura si lasa referinta goala — deci la inchidere nu mai era unde sa
      // fie readus focusul.
      if (restoreFocusTo.current === null) restoreFocusTo.current = document.activeElement;
      if (!dialog.open) dialog.showModal();
      // Derularea din spate se opreste cat timp fereastra e deschisa. Fara asta,
      // pe telefon degetul care crede ca deruleaza optiunile misca meniul de
      // dedesubt, iar la inchidere clientul e in alta parte a paginii.
      const previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previous;
        // Focusul se readuce si AICI, nu doar pe evenimentul `close`: cand
        // parintele demonteaza fereastra (calea „Adauga in cos"), elementul iese
        // din DOM fara sa emita `close`, deci handler-ul de acolo nu ruleaza si
        // focusul cade pe `<body>`.
        //
        // Se AMANA intr-un microtask: React ruleaza curatenia INAINTE sa scoata
        // nodul din DOM, iar browserul muta focusul pe `<body>` cand dialogul
        // modal chiar dispare. Microtask, nu `requestAnimationFrame`: ruleaza
        // dupa comitere si nu depinde de bucla de randare, care intr-o fila de
        // fundal nu ruleaza.
        const target = restoreFocusTo.current;
        queueMicrotask(() => {
          // Fereastra inca in pagina = nu a fost o inchidere, ci reasezarea
          // efectului (`<StrictMode>` in dezvoltare). Focusul nu se atinge.
          if (dialog.isConnected) return;
          restoreFocusTo.current = null;
          if (target instanceof HTMLElement && target.isConnected) target.focus();
        });
      };
    }

    if (dialog.open) dialog.close();
    return undefined;
  }, [open]);

  /*
    Escape e SINGURA cale de inchidere pe care nu o putem declansa noi: browserul
    o trateaza intern. Se asculta ambele evenimente pe care le emite — `cancel`
    (inainte) si `close` (dupa) — fiindca amandoua duc in acelasi loc, iar
    `onClose` e idempotent.

    Butonul × si clicul pe fundal NU trec pe aici: apeleaza direct `onClose`.
    Varianta anterioara le trecea prin `dialog.close()` si astepta evenimentul
    `close` sa sincronizeze React; exista medii in care nu se emite deloc, iar
    fereastra ramanea montata cu `overflow: hidden` pe `<body>`.
  */
  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    const dismiss = (): void => onClose();
    dialog.addEventListener("cancel", dismiss);
    dialog.addEventListener("close", dismiss);
    return () => {
      dialog.removeEventListener("cancel", dismiss);
      dialog.removeEventListener("close", dismiss);
    };
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      aria-label={title}
      // Clicul pe fundal ajunge pe elementul `<dialog>` insusi: continutul e
      // intr-un copil, deci `currentTarget === target` inseamna "in afara".
      //
      // Se cere insa ca AMBELE capete ale gestului sa fie pe fundal. `click` se
      // emite pe cel mai apropiat stramos comun al lui `mousedown` si `mouseup`,
      // iar pentru o tragere care incepe in continut si se termina afara acela e
      // chiar `<dialog>`. Fara conditia de mai jos, o simpla selectare de text
      // in descrierea produsului inchidea fereastra si stergea optiunile alese.
      onPointerDown={(event) => {
        pressedBackdrop.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (pressedBackdrop.current && event.target === event.currentTarget) onClose();
      }}
      className={[
        // Jos pe telefon (degetul ajunge acolo), centrat pe ecran mare.
        // Fereastra insasi NU se deruleaza (`overflow-hidden`, peste `auto`-ul
        // din browser): se deruleaza doar corpul, iar bara de actiuni ramane
        // lipita jos. `open:flex` (nu `flex`) — altfel `display` ar bate
        // `display: none` al dialogului inchis.
        "da-sheet m-0 max-h-[92dvh] w-full overflow-hidden border-0 bg-canvas p-0 text-ink shadow-2xl open:flex open:flex-col",
        "rounded-t-[1.25rem] sm:rounded-card",
        "mt-auto sm:m-auto sm:border sm:border-line",
        size === "lg" ? "sm:max-w-3xl" : "sm:max-w-xl",
        "backdrop:bg-black/50 backdrop:backdrop-blur-[2px]",
      ].join(" ")}
    >
      {/* Inaltimea vine din fereastra (flex), nu din inca un `92dvh`: acela nu
          scadea chenarul de pe ecran mare si lasa fereastra sa se deruleze 2px. */}
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Manerul: semnalul vizual ca foaia se poate inchide. Apasabil, ca sa
            existe o tinta mare de inchidere si cand antetul lipseste. */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Închide"
          className="da-handle-wrap sm:hidden"
        >
          <span className="da-handle" />
        </button>

        {hideHeader ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Închide"
            className="absolute right-3 top-3 z-10 grid h-10 w-10 place-items-center rounded-full bg-canvas/90 text-ink shadow-md backdrop-blur sm:top-3"
          >
            <IconClose size={18} />
          </button>
        ) : (
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <h2 className="text-lg font-bold text-ink">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Închide"
              className="-mr-2 grid h-10 w-10 shrink-0 place-items-center rounded-full text-ink-muted hover:bg-surface hover:text-ink"
            >
              <IconClose size={20} />
            </button>
          </div>
        )}

        {/* `overscroll-contain`: capatul listei nu mai trece derularea mai departe. */}
        <div className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${flush ? "" : "px-4 py-4"}`}>{children}</div>

        {footer === undefined ? null : (
          <div className="da-sheet-footer border-t border-line bg-canvas px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {footer}
          </div>
        )}
      </div>
    </dialog>
  );
}
